// per docs/shape-uc-dash-01-grafana-is-the-engine.md §2, docs/ui-shape-grafana-authoring.md §2/§6,
// caiet UC-DASH-03/04/07/11/12 — every Grafana address the SIMSM shell embeds or opens is built
// here, so the two presentations the owner separated can be read side by side.
//
// The owner ruled on 2026-09-01 (spec 002 §"Owner rulings, 2026-09-01" #5): "Tablouri de bord"
// reads — kiosk, no editing chrome — and "Gestiune tablouri" edits, beside the role sharing that
// cannot leave SIMSM, because analytics.dashboard_config.role_scope is the only source the
// security gate reads and Grafana OSS has no notion of a SIMSM role.
//
// DISCLOSED WITH THAT RULING, AND DISCLOSED HERE ON PURPOSE — READ THIS BEFORE TRUSTING IT:
// view and edit for the same person are separated by these URL parameters, **not by
// permissions**. `kiosk` hides Grafana's own chrome (search bar, breadcrumb, Share, the panel
// menus); it takes nothing away from the session behind the frame. The System Administrator
// reaches Grafana as an **Editor on both paths**, because the proxy maps SYSTEM_ADMIN → Editor
// on every request it forwards (apps/bff/src/grafana-proxy/grafana-org-role.ts), so an
// administrator who deletes `kiosk` from the address of the read-only page is authoring again.
// Nothing in this file is an access control and nobody may later mistake it for one.
//
// The real access controls are elsewhere and are untouched by the split: the proxy authorises
// the uid against analytics.dashboard_config.role_scope before Grafana sees the request
// (apps/bff/src/grafana-proxy/grafana-proxy.middleware.ts), and the read API repeats the check on
// the deep-link path (apps/bff/src/dashboards/dashboards.controller.ts). Real permission
// separation — a second Grafana identity for the same person, Viewer for reading and Editor for
// authoring — is a larger change, and it was not ruled.

import { addCalendarDays, chisinauDayStartMs } from '@simsm/shared-types';

// The interval vocabulary the administrator picks from (UC-DASH-03), in Grafana's own `from`
// syntax so the frame opens on it without a translation layer in between.
export const RANGE_FROM: Record<string, string> = {
  'last-7-days': 'now-7d',
  'last-30-days': 'now-30d',
  'last-90-days': 'now-90d',
  'last-12-months': 'now-12M',
};

// A viewer's own interval (UC-DASH-03/04): two Chisinau calendar days, both inclusive.
export interface FrameDates {
  from: string;
  to: string;
}

// Grafana keeps a URL refresh only when it is one of the dashboard's refresh intervals and
// otherwise falls back to the first of them, 5 seconds by default. So the period is written in
// Grafana's own spelling, rounded up to the nearest step: never shorter than the one chosen
// (docs/shape-ops-dashboard-time-range.md §2.5).
const GRAFANA_REFRESH_STEPS: readonly (readonly [number, string])[] = [
  [30, '30s'],
  [60, '1m'],
  [300, '5m'],
  [900, '15m'],
  [1800, '30m'],
  [3600, '1h'],
];

export function grafanaRefreshInterval(seconds: number): string {
  return GRAFANA_REFRESH_STEPS.find(([limit]) => limit >= seconds)?.[1] ?? '1h';
}

// The reading presentation (UC-DASH-11/12): same-origin path, so the request carries the SIMSM
// session and the proxy decides whether this role may open this uid before Grafana ever sees it.
//
// UC-DASH-04: `kiosk` alone leaves Grafana's time picker in the frame, and it has no ceiling of
// its own, so one click on its zoom-out would defeat the interval the definition capped (the
// proxy now refuses such a query, docs/shape-ops-dashboard-time-range.md §2.3, but the panel
// would show the refusal). `_dash.hideTimePicker` removes that picker and leaves the variables,
// which the viewer IS allowed to change (UC-DASH-01/05). The page renders the interval and
// refresh controls instead.
//
// `refreshIntervalSeconds` null is "Off": no `refresh` at all, and the provisioned dashboards save
// none of their own, so nothing refreshes.
//
// CF-76 / Q-1-11: `dashboardSlug` is the SIMSM dashboard record the viewer opened. A Grafana uid can
// sit behind a general record and behind a Specific Observer's own record at once, and only the
// record says which of the two this frame is. The browser repeats this address as the Referer of
// every panel query, where the proxy reads the parameter (apps/bff/src/grafana-proxy/
// grafana-request-target.ts, `dashboardContextOf`); it is a claim the proxy checks against the
// records the role holds, not an access control.
export const DASHBOARD_CONTEXT_PARAM = 'simsm_dashboard';

export function grafanaViewSrc(
  uid: string,
  range: string | FrameDates,
  refreshIntervalSeconds: number | null,
  dashboardSlug?: string,
): string {
  const interval =
    typeof range === 'string'
      ? { from: RANGE_FROM[range] ?? 'now-30d', to: 'now' }
      : {
          from: String(chisinauDayStartMs(range.from)),
          to: String(chisinauDayStartMs(addCalendarDays(range.to, 1)) - 1),
        };
  const params = new URLSearchParams({ theme: 'light', ...interval });
  if (refreshIntervalSeconds !== null) {
    params.set(
      'refresh',
      grafanaRefreshInterval(refreshIntervalSeconds > 0 ? refreshIntervalSeconds : 300),
    );
  }
  params.set('_dash.hideTimePicker', 'true');
  if (dashboardSlug) params.set(DASHBOARD_CONTEXT_PARAM, dashboardSlug);
  // `kiosk` is a valueless flag: Grafana 11.3 reads `?kiosk` as on and `?kiosk=` as off, and
  // URLSearchParams can only produce the second. Verified in the browser — with `kiosk=` the
  // frame keeps Grafana's search bar, breadcrumb and Share button inside the SIMSM page.
  return `/grafana/d/${encodeURIComponent(uid)}?kiosk&${params.toString()}`;
}

// What the authoring surface points at: Grafana's own list of dashboards, one dashboard, or the
// blank one. `browse` is the default target of the editor beside the sharing table.
export type GrafanaEditorTarget =
  | { kind: 'browse' }
  | { kind: 'new' }
  | { kind: 'dashboard'; uid: string };

// The authoring presentation (caiet §6 #7, UC26-01, UC-DASH-08): Grafana with its chrome — the
// panel editor, Save, Add panel — because this is where the administrator draws the dashboard
// they are about to share. No `kiosk`: that is the whole of the difference, and see the
// disclosure at the top of this file for what it does and does not mean.
export function grafanaEditorSrc(target: GrafanaEditorTarget): string {
  const theme = 'theme=light';
  switch (target.kind) {
    case 'new':
      return `/grafana/dashboard/new?${theme}`;
    case 'dashboard':
      return `/grafana/d/${encodeURIComponent(target.uid)}?${theme}`;
    default:
      return `/grafana/dashboards?${theme}`;
  }
}
